const INSTALL_STATE_EVENT = "firmaflow:pwa-install-state";
const INSTALLED_KEY = "firmaflow_pwa_installed";
const LEGACY_INSTALLED_KEY = "pwa-installed";
const DISMISSED_KEY = "firmaflow_pwa_install_dismissed";
const LEGACY_DASHBOARD_DISMISSED_KEY = "pwa_install_dismissed";
const LEGACY_FLOATING_DISMISSED_KEY = "pwa-install-dismissed";
const DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000;

let initialized = false;
let deferredPrompt = null;
let displayModeQuery = null;

const hasWindow = () => typeof window !== "undefined";

const readStorage = (key) => {
  if (!hasWindow()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key, value) => {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable in private browsing or locked-down webviews.
  }
};

const removeStorage = (key) => {
  if (!hasWindow()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage cleanup failures.
  }
};

const notifyStateChanged = () => {
  if (!hasWindow()) return;
  window.dispatchEvent(
    new CustomEvent(INSTALL_STATE_EVENT, { detail: getPwaInstallState() })
  );
};

export const isIosDevice = () => {
  if (!hasWindow()) return false;
  const userAgent = window.navigator.userAgent || "";
  const platform = window.navigator.platform || "";
  const hasTouch = window.navigator.maxTouchPoints > 1;

  return (
    /iPad|iPhone|iPod/.test(userAgent) ||
    (platform === "MacIntel" && hasTouch)
  );
};

export const isStandalonePwa = () => {
  if (!hasWindow()) return false;

  const standaloneModes = [
    "(display-mode: standalone)",
    "(display-mode: fullscreen)",
    "(display-mode: minimal-ui)",
  ];

  return (
    standaloneModes.some((query) => window.matchMedia?.(query).matches) ||
    window.navigator.standalone === true ||
    window.document.referrer.startsWith("android-app://")
  );
};

export const isPwaInstalled = () => {
  return (
    isStandalonePwa() ||
    readStorage(INSTALLED_KEY) === "true" ||
    readStorage(LEGACY_INSTALLED_KEY) === "true"
  );
};

export const isPwaInstallDismissed = () => {
  const dismissedValues = [
    readStorage(DISMISSED_KEY),
    readStorage(LEGACY_DASHBOARD_DISMISSED_KEY),
    readStorage(LEGACY_FLOATING_DISMISSED_KEY),
  ];

  return dismissedValues.some((value) => {
    const dismissedAt = Number(value);
    return (
      Number.isFinite(dismissedAt) &&
      dismissedAt > 0 &&
      Date.now() - dismissedAt < DISMISS_COOLDOWN_MS
    );
  });
};

export const getPwaInstallState = () => {
  const isInstalled = isPwaInstalled();
  const isIOS = isIosDevice();
  const isPromptAvailable = Boolean(deferredPrompt);

  return {
    dismissed: isPwaInstallDismissed(),
    isInstalled,
    isIOS,
    isPromptAvailable,
    isStandalone: isStandalonePwa(),
    canInstall: !isInstalled && (isPromptAvailable || isIOS),
  };
};

export const markPwaInstalled = () => {
  deferredPrompt = null;
  writeStorage(INSTALLED_KEY, "true");
  writeStorage(LEGACY_INSTALLED_KEY, "true");
  removeStorage(DISMISSED_KEY);
  removeStorage(LEGACY_DASHBOARD_DISMISSED_KEY);
  removeStorage(LEGACY_FLOATING_DISMISSED_KEY);
  notifyStateChanged();
};

export const dismissPwaInstall = () => {
  const dismissedAt = Date.now().toString();
  writeStorage(DISMISSED_KEY, dismissedAt);
  writeStorage(LEGACY_DASHBOARD_DISMISSED_KEY, dismissedAt);
  writeStorage(LEGACY_FLOATING_DISMISSED_KEY, dismissedAt);
  notifyStateChanged();
};

export const initPwaInstall = () => {
  if (!hasWindow() || initialized) return;
  initialized = true;

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event;
    notifyStateChanged();
  });

  window.addEventListener("appinstalled", () => {
    markPwaInstalled();
  });

  window.addEventListener("storage", (event) => {
    const trackedKeys = [
      INSTALLED_KEY,
      LEGACY_INSTALLED_KEY,
      DISMISSED_KEY,
      LEGACY_DASHBOARD_DISMISSED_KEY,
      LEGACY_FLOATING_DISMISSED_KEY,
    ];

    if (trackedKeys.includes(event.key)) {
      notifyStateChanged();
    }
  });

  displayModeQuery = window.matchMedia?.("(display-mode: standalone)");
  if (displayModeQuery?.addEventListener) {
    displayModeQuery.addEventListener("change", notifyStateChanged);
  } else if (displayModeQuery?.addListener) {
    displayModeQuery.addListener(notifyStateChanged);
  }

  if (isStandalonePwa()) {
    markPwaInstalled();
  } else {
    notifyStateChanged();
  }
};

export const subscribeToPwaInstall = (callback) => {
  if (!hasWindow()) return () => {};

  initPwaInstall();

  const handler = (event) => {
    callback(event.detail || getPwaInstallState());
  };

  window.addEventListener(INSTALL_STATE_EVENT, handler);
  callback(getPwaInstallState());

  return () => {
    window.removeEventListener(INSTALL_STATE_EVENT, handler);
  };
};

export const promptPwaInstall = async () => {
  initPwaInstall();

  if (isPwaInstalled()) {
    return { status: "installed" };
  }

  if (!deferredPrompt) {
    if (isIosDevice()) {
      return { status: "manual-ios" };
    }

    return { status: "unavailable" };
  }

  const promptEvent = deferredPrompt;
  deferredPrompt = null;
  notifyStateChanged();

  try {
    const promptResult = promptEvent.prompt();

    if (promptResult && typeof promptResult.then === "function") {
      await promptResult;
    }

    const choice = await promptEvent.userChoice;
    const outcome = choice?.outcome || "dismissed";

    if (outcome === "accepted") {
      markPwaInstalled();
      return { status: "accepted", outcome };
    }

    notifyStateChanged();
    return { status: "dismissed", outcome };
  } catch (error) {
    notifyStateChanged();
    return { status: "error", error };
  }
};
